import React from 'react'
import { Box, css } from '@hanzo/ui'
import Link from 'next/link'

import { type TShirtSize } from '@hanzo/ui/types'
import { Logo as Mark, Wordmark } from '@luxfi/logo'

/**
 * mark     — the triangle alone
 * wordmark — the LUX letterforms alone
 * full     — the mark beside the wordmark
 *
 * Both halves come from @luxfi/logo, which is the one home for Lux brand art.
 * This file used to draw its own triangle and set the letters "LUX" in the body
 * font, so every Lux site wore typed text where the wordmark belongs — and no
 * variant could fix it, because neither the mark nor the wordmark was here to
 * ask for. Art lives in the brand package; this composes it.
 */
type LogoVariant = 'mark' | 'wordmark' | 'full'

/** Mark height in px, and the wordmark height that stands with it. */
const SIZE: Record<string, { mark: number, word: number, gap: string }> = {
  xl: { mark: 40, word: 22, gap: 'gap-4' },
  lg: { mark: 40, word: 22, gap: 'gap-4' },
  md: { mark: 32, word: 18, gap: 'gap-3' },
  sm: { mark: 24, word: 13, gap: 'gap-2' },
  xs: { mark: 16, word: 9, gap: 'gap-1.5' },
}

const Logo: React.FC<{
  size?: TShirtSize
  variant?: LogoVariant
  onClick?: () => void
  href?: string
  outerClx?: string
}> = ({
  size = 'md',
  href, // no default please!
  outerClx = '',
  variant = 'full',
  onClick,
}) => {
  const s = SIZE[size as string] ?? SIZE.md
  const clx = 'flex flex-row items-center ' + s.gap + ' '
    + (href ? 'hover:opacity-80 cursor-pointer ' : 'cursor-default ')
    + outerClx

  // White on both counts: the Lux ground is black and the brand's accent is
  // white, so the mark and the letters carry the same value.
  const art = (<>
    {variant !== 'wordmark' && <Mark variant='white' size={s.mark} />}
    {variant !== 'mark' && <Wordmark height={s.word} />}
  </>)

  return href ? (
    <Link href={href} style={css(clx)} onClick={onClick}>{art}</Link>
  ) : (
    <Box tag="span" className={clx} onClick={onClick}>{art}</Box>
  )
}

export {
  Logo as default,
  type LogoVariant
}
